/* ==========================================================================
   BR4NDOM — Coming Soon
   - Adds .is-loaded once the DOM is ready (drives top-content fade).
   - Watches video playback and, when the video reaches the last
     `data-reveal-before-end` seconds, adds .is-bottom-revealed which
     fades the email form / tagline in. Once revealed, it stays revealed.
   - Falls back gracefully if the video cannot load or autoplay is blocked.
   ========================================================================== */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.brandomBound === '1') return;
    root.dataset.brandomBound = '1';

    var video = root.querySelector('.brandom-coming-soon__video');
    var revealDelay = parseInt(root.getAttribute('data-bottom-reveal-delay'), 10);
    if (!isFinite(revealDelay) || revealDelay < 0) revealDelay = 3200;

    var forceReveal = root.getAttribute('data-force-reveal') === 'true';

    var markLoaded = function () {
      window.requestAnimationFrame(function () {
        root.classList.add('is-loaded');
      });
    };

    var revealBottom = function () {
      if (root.classList.contains('is-bottom-revealed')) return;
      root.classList.add('is-bottom-revealed');
    };

    // 1) Drive the top-content fade as soon as the DOM is parsed.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', markLoaded, { once: true });
    } else {
      markLoaded();
    }

    // 2) If the form was just submitted (success or error), reveal immediately
    //    so the user sees the result without waiting.
    if (forceReveal) {
      revealBottom();
    } else {
      // Reveal the bottom shortly after load — right after the top copy
      // finishes its fade-in, so the form arrives with the headline.
      window.setTimeout(revealBottom, revealDelay);
    }

    if (!video) return;

    // Force the autoplay-eligible attributes via JS too — some mobile
    // browsers only honor them when set on the property, not just HTML.
    try {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
    } catch (_) { /* no-op */ }

    var videoReady = false;
    var played = false;

    var attemptPlay = function () {
      if (played) return;
      var p = video.play && video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          played = true;
        }).catch(function () {
          // Autoplay blocked — wire a one-shot user-gesture retry so the
          // video starts on the very first tap/scroll without any visible
          // controls or interaction prompt.
          var resume = function () {
            video.play().then(function () { played = true; }).catch(function () {});
            window.removeEventListener('touchstart', resume, true);
            window.removeEventListener('touchend', resume, true);
            window.removeEventListener('click', resume, true);
            window.removeEventListener('scroll', resume, true);
          };
          window.addEventListener('touchstart', resume, { once: true, passive: true, capture: true });
          window.addEventListener('touchend', resume, { once: true, passive: true, capture: true });
          window.addEventListener('click', resume, { once: true, capture: true });
          window.addEventListener('scroll', resume, { once: true, passive: true, capture: true });
        });
      } else {
        played = true;
      }
    };

    var onReady = function () {
      if (videoReady) return;
      videoReady = true;
      attemptPlay();
    };

    if (video.readyState >= 2) {
      onReady();
    } else {
      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('canplay', onReady, { once: true });
      // Some mobile browsers stall preload until told — nudge it.
      try { video.load(); } catch (_) { /* no-op */ }
    }

    // Re-attempt when the tab becomes visible again (saves the day after
    // the page was opened in a background tab on mobile).
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && !played) attemptPlay();
    });

    // Error / safety nets.
    video.addEventListener('error', function () {
      root.classList.add('is-video-failed');
      revealBottom();
    });

    // If video metadata never arrives, still unblock the UI after 6s.
    window.setTimeout(function () {
      if (!videoReady) {
        root.classList.add('is-loaded');
        revealBottom();
      }
    }, 6000);
  }

  /* ------------------------------------------------------------------
     AJAX enhancement for the email form.
     Intercepts submit so the button just transitions from
     "Notify me" → "Submitting…" → "Submitted ✓" without a full page
     navigation. If Shopify responds with its bot-challenge redirect,
     we fall back to a normal form submit so the user isn't stuck.
     ------------------------------------------------------------------ */
  function enhanceForm(root) {
    var form = root.querySelector('.brandom-coming-soon__form');
    if (!form || form.dataset.brandomFormBound === '1') return;
    form.dataset.brandomFormBound = '1';

    var button = form.querySelector('.brandom-coming-soon__submit');
    var input = form.querySelector('.brandom-coming-soon__input');
    if (!button || !input) return;

    var originalButtonHTML = button.innerHTML;
    var loadingLabel = root.getAttribute('data-loading-label') || 'Submitting';
    var successLabel = root.getAttribute('data-success-label') || 'Submitted';

    var setLoading = function () {
      button.disabled = true;
      button.classList.add('is-loading');
      button.innerHTML =
        '<span>' + loadingLabel + '</span>' +
        '<span class="brandom-coming-soon__spinner" aria-hidden="true"></span>';
      input.setAttribute('readonly', 'readonly');
      root.classList.add('brandom-coming-soon--submitting');
    };

    var setSuccess = function () {
      button.classList.remove('is-loading');
      button.classList.add('is-success');
      button.disabled = true;
      button.innerHTML =
        '<span>' + successLabel + '</span>' +
        '<svg class="brandom-coming-soon__arrow" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M5 12.5l4 4 10-10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
        '</svg>';
      input.setAttribute('readonly', 'readonly');
      root.classList.remove('brandom-coming-soon--submitting');
      root.classList.add('brandom-coming-soon--inline-success');
    };

    var setError = function (msg) {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.innerHTML = originalButtonHTML;
      input.removeAttribute('readonly');
      root.classList.remove('brandom-coming-soon--submitting');

      var errEl = form.querySelector('[data-brandom-ajax-error]');
      if (!errEl) {
        errEl = document.createElement('p');
        errEl.setAttribute('data-brandom-ajax-error', '');
        errEl.className =
          'brandom-coming-soon__message brandom-coming-soon__message--error';
        errEl.setAttribute('role', 'alert');
        form.appendChild(errEl);
      }
      errEl.textContent = msg || 'Something went wrong. Please try again.';
    };

    form.addEventListener('submit', function (event) {
      if (!input.value.trim() || (input.checkValidity && !input.checkValidity())) {
        return;
      }
      event.preventDefault();

      setLoading();

      var formData = new FormData(form);

      fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'text/html' },
        credentials: 'same-origin',
        redirect: 'follow'
      })
        .then(function (response) {
          if (response.url && /\/challenge(\b|[/?])/i.test(response.url)) {
            // Shopify bot-protection interrupted — fall back to native flow
            // so the visitor can complete the challenge.
            form.submit();
            return null;
          }
          if (!response.ok) {
            throw new Error('HTTP ' + response.status);
          }
          return response.text();
        })
        .then(function (text) {
          if (text === null) return;
          // Sniff the response for Shopify form error markers; if none, treat
          // as success.
          var errMatch = text.match(/<ul[^>]*class="[^"]*errors[^"]*"[^>]*>([\s\S]*?)<\/ul>/i);
          if (errMatch) {
            // Grab first <li> text content for a short message.
            var liMatch = errMatch[1].match(/<li[^>]*>([\s\S]*?)<\/li>/i);
            var msg = liMatch ? liMatch[1].replace(/<[^>]+>/g, '').trim() : '';
            setError(msg);
            return;
          }
          setSuccess();
        })
        .catch(function () {
          // Network / CORS / other failure — fall back to native submit so
          // the subscription still has a chance to go through.
          form.submit();
        });
    });
  }

  function boot() {
    var sections = document.querySelectorAll('.brandom-coming-soon');
    for (var i = 0; i < sections.length; i++) {
      init(sections[i]);
      enhanceForm(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor support — re-init on section load.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target && event.target.querySelector
      ? event.target.querySelector('.brandom-coming-soon')
      : null;
    if (section) {
      section.dataset.brandomBound = '';
      init(section);
      enhanceForm(section);
    }
  });
})();
